import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { fetch_all_with_count } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
} from '@mui/material';

import {
  Dt,
  Typography
} from '../../../components/v2/styled';
import { Base } from './Base';

import { ConsentLog } from '../../../models/ConsentLog';

type Props = {
  open: boolean;
  setOpen: (b:boolean) => void;
  consentId?: number;
  isCoach?: boolean;
};

export const ConsentLogs: FunctionComponent<Props> = ({open, setOpen, consentId, isCoach}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const theme = useTheme();
  // const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // const [open, setOpen] = useState<boolean>(false);
  
  let count = 0;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("id DESC");

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["consent_logs", consentId, {page: page, order: order}],
    queryFn: () =>
      fetch_all_with_count<ConsentLog>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'consent_log',
          fields: ['id', 'created_at', 'consent', 'user', 'comment', 'is_filemaker', 'is_flagged'],
          sub_fields: {
            consent: ['id', 'enabled', 'audits'],
            user: ['id', 'full_name']
          },
          order: order,
          per_page: 20,
          page: page,
          filter: {
            search: '',
            advanced: {
              consent_id: consentId
            }
          }
        },
        login
      ),
    select: (d) => {
      count = d.count;
      return d.result;
    },
    enabled: !!consentId,
  });







  // useEffect(() => {
  //   setOpen(!hidden);
  // }, [hidden]);

  return (<>
    <Base
      name="alert"
      hidden={!open}
      title={t("general.popup.consent_logs.title")}
      content={<Grid container spacing={1} sx={{marginTop: 0}}>
        {objectsLoading && <Grid item xs={12} sx={{textAlign: 'center'}}>
          <CircularProgress />
          <Typography sx={{
            fontSize: '0.9rem',
            fontStyle: 'italic'
          }}>{t("general.popup.consent_logs.loading_notification")}</Typography>
        </Grid>}
        {objectsSuccess && count < 1 && <Grid item xs={12} sx={{textAlign: 'center'}}>
          <Typography sx={{fontStyle: 'italic'}}>{t("shared.terms.no_results", "Er zijn geen resultaten beschikbaar")}</Typography>
        </Grid>}
        {objectsSuccess && count > 0 && <Grid item xs={12}>
          <Typography variant="subtitle1">{t("general.popup.consent_logs.last_20_changes")}</Typography>
        </Grid>}
        {objectsSuccess && count > 0 && objects.map(ob => <Grid item xs={12}>
          <Box sx={{
            border: '1px solid transparent',
            borderColor: theme.palette.tertiary?.main,
            padding: 1,
            borderRadius: 1,
            position: 'relative'
          }}>
            <Typography variant="caption" sx={{fontWeight: 'bold'}}><Dt d={ob.created_at} f="dd/LL/y - t" /></Typography>
            <Divider sx={{marginTop: 0.5, marginBottom: 0.5}} />
            <Typography variant="body1">{t("general.popup.consent_logs.field.actor")}</Typography>
            <Typography variant="caption" sx={{paddingLeft: 2}}>{ob.user?.full_name || 'System'}</Typography>
            <Divider sx={{marginTop: 0.5, marginBottom: 0.5}} />
            <Typography variant="body1">{t("general.popup.consent_logs.field.comment")}</Typography>
            <Typography variant="caption" sx={{paddingLeft: 2}}>{ob.comment}</Typography>

            {!!isCoach && (ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at).length > 0 && <>
              <Divider />
              <Typography variant="body1">{t("general.popup.consent_logs.field.audit")}</Typography>
              {Object.keys(((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at)[0] || {}).audited_changes).map(kk => <>
                {(((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at) || [])[0] || {}).action === 'create' && <>
                  <Typography variant="caption" sx={{paddingLeft: 2}}>{kk}</Typography>
                  <Typography variant="caption" sx={{paddingLeft: 4}}>{(((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at) || [])[0] || {}).audited_changes[kk]?.toString()}</Typography><br />
                </>}
                {(((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at) || [])[0] || {}).action !== 'create' && <>
                  <Typography variant="caption" sx={{paddingLeft: 2}}>{kk}</Typography><br />
                  <Typography variant="caption" sx={{paddingLeft: 4}}>from: {(((((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at) || [])[0] || {}).audited_changes[kk] || [])[0] || '').toString()}</Typography><br />
                  <Typography variant="caption" sx={{paddingLeft: 4}}>to: {(((((ob.consent.audits || []).filter(fff => fff.created_at === ob.created_at) || [])[0] || {}).audited_changes[kk] || [])[1] || '').toString()}</Typography><br />
                </>}
              </>)}
            </>}

            {!!isCoach && ob.is_filemaker && <Avatar
              src="https://cdn.kangacoders.com/direct/nutrilink_eu/images/vinkje_blauw_groen.png"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: '25px',
                height: '25px'
              }}
            />}
          </Box>
        </Grid>)}
      </Grid>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Button autoFocus onClick={() => {setOpen(false);}}>{t("shared.buttons.close")}</Button>
      </DialogActions>}
      open={open}
      setOpen={setOpen}
    />
  </>);
}